package io.horion.auth.presentation.ui.components.profile

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.auth.domain.models.AppLanguage
import io.horion.auth.presentation.ui.components.auth.AuthButton
import io.horion.auth.presentation.ui.components.auth.AuthTextField
import io.horion.auth.presentation.ui.theme.AuthColors
import io.horion.auth.presentation.ui.theme.AuthTheme
import io.horion.auth.presentation.ui.theme.AuthTypography
import io.horion.auth.presentation.viewmodel.AuthViewModel
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*

@Composable
fun ProfilePersonalInfoSection(viewModel: AuthViewModel) {
    val colorMode = ColorMode.current
    val colors = AuthTheme.colors(colorMode)
    val strings = AppLanguage.strings
    val state = viewModel.uiState

    val isProfileAlreadyCreated = state.isProfileCreated

    // 2. Champs saisis en cours pour l'édition/affichage
    val firstName = state.userFirstName?.takeIf { it.isNotBlank() }
    val lastName = state.userLastName?.takeIf { it.isNotBlank() }
    val userEmail = state.userEmail?.takeIf { it.isNotBlank() }
    val userPhone = state.userPhone?.takeIf { it.isNotBlank() }
    val address = state.address?.takeIf { it.isNotBlank() }

    // 3. Formatage dynamique du nom d'affichage pour la lecture
    val displayName = when {
        firstName != null || lastName != null -> {
            listOfNotNull(firstName, lastName).joinToString(" ").replaceFirstChar { it.uppercase() }
        }
        userEmail != null -> {
            userEmail.substringBefore("@").replaceFirstChar { it.uppercase() }
        }
        userPhone != null -> userPhone
        else -> strings.emptyField
    }

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(16.px)
            .backgroundColor(colors.surface)
            .border(1.px, LineStyle.Solid, colors.border)
            .styleModifier { property("box-shadow", "0 1px 3px rgba(0,0,0,0.04)") }
    ) {
        // En-tête de section
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(leftRight = 24.px, topBottom = 20.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.gap(10.px)) {
                FaUser(modifier = Modifier.fontSize(18.px).color(colors.primary))
                SpanText(
                    strings.profileSectionInfo,
                    modifier = Modifier
                        .fontSize(AuthTypography.body)
                        .fontWeight(FontWeight.SemiBold)
                        .color(colors.onBackground)
                )
            }
            if (!state.isEditingProfile) {
                Row(
                    modifier = Modifier
                        .padding(leftRight = 14.px, topBottom = 7.px)
                        .borderRadius(8.px)
                        .border(1.px, LineStyle.Solid, colors.border)
                        .cursor(Cursor.Pointer)
                        .gap(6.px)
                        .onClick { viewModel.startEditProfile() },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    FaPen(modifier = Modifier.fontSize(12.px).color(colors.primary))
                    SpanText(
                        if (isProfileAlreadyCreated) strings.profileEditButton else "Compléter",
                        modifier = Modifier
                            .fontSize(AuthTypography.small)
                            .fontWeight(FontWeight.Medium)
                            .color(colors.primary)
                    )
                }
            }
        }

        Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(colors.border))

        // ── Feedback Succès / Erreur ──────────────────────────────────────────
        if (state.profileSaveSuccess) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(leftRight = 24.px, topBottom = 12.px)
                    .gap(8.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                FaCircleCheck(modifier = Modifier.color(colors.success).fontSize(14.px))
                SpanText(
                    strings.profileUpdateSuccess,
                    modifier = Modifier.fontSize(AuthTypography.small).color(colors.success)
                )
            }
            Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(colors.border))
        }

        val errorMessage = state.profileSaveError
        if (!errorMessage.isNullOrBlank()) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(leftRight = 24.px, topBottom = 12.px)
                    .gap(8.px),
                verticalAlignment = Alignment.CenterVertically
            ) {
                FaCircleExclamation(modifier = Modifier.color(colors.error).fontSize(14.px))
                SpanText(
                    errorMessage,
                    modifier = Modifier.fontSize(AuthTypography.small).color(colors.error)
                )
            }
            Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(colors.border))
        }

        if (state.isEditingProfile) {
            // Mode Édition / Création
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(24.px)
                    .gap(14.px)
            ) {
                Row(modifier = Modifier.fillMaxWidth().gap(12.px)) {
                    AuthTextField(
                        modifier = Modifier.weight(1),
                        value = state.firstName.orEmpty(),
                        onValueChange = { viewModel.onFirstNameChange(it) },
                        placeholder = strings.firstNameLabel,
                        leadingIcon = { FaUser(modifier = Modifier.color(colors.secondary)) }
                    )
                    AuthTextField(
                        modifier = Modifier.weight(1),
                        value = state.lastName.orEmpty(),
                        onValueChange = { viewModel.onLastNameChange(it) },
                        placeholder = strings.lastNameLabel,
                        leadingIcon = { FaUser(modifier = Modifier.color(colors.secondary)) }
                    )
                }

//                AuthTextField(
//                    modifier = Modifier.fillMaxWidth(),
//                    value = state.address.orEmpty(),
//                    onValueChange = { viewModel.onAddressChange(it) },
//                    placeholder = strings.addressLabel,
//                    leadingIcon = { FaLocationDot(modifier = Modifier.color(colors.secondary)) }
//                )

                // Boutons d'action
                Row(modifier = Modifier.fillMaxWidth().gap(12.px).padding(top = 4.px)) {
                    AuthButton(
                        text = strings.profileCancelButton,
                        onClick = { viewModel.cancelEditProfile() },
                        loading = false,
                        disabled = state.isSavingProfile,
                        modifier = Modifier.weight(1).styleModifier { property("opacity", "0.6") }
                    )

                    // ✅ Aiguillage corrigé : basé sur l'état réel du profil en BDD
                    AuthButton(
                        text = if (isProfileAlreadyCreated) strings.profileSaveButton else "Créer le profil",
                        onClick = {
                            if (isProfileAlreadyCreated) {
                                viewModel.updatePersonalInfo(
                                    newFirstName = state.firstName.orEmpty(),
                                    newLastName = state.lastName.orEmpty()
                                )
                            } else {
                                viewModel.createProfile(
                                    firstName = state.firstName.orEmpty(),
                                    lastName = state.lastName.orEmpty(),
                                    avatar = state.avatarUrl,
                                    is2FAEnabled = state.is2FAEnabled
                                )
                            }
                        },
                        loading = state.isSavingProfile,
                        modifier = Modifier.weight(1)
                    )
                }
            }
        } else {
            // Mode Lecture
            InfoRow(
                label = strings.profileName,
                value = displayName,
                isLast = false,
                colors = colors
            )
            InfoRow(
                label = strings.emailLabel,
                value = userEmail ?: strings.emptyField,
                isLast = false,
                colors = colors
            )
            InfoRow(
                label = strings.phoneLabel,
                value = userPhone ?: strings.emptyField,
                isLast = false,
                colors = colors
            )
//            InfoRow(
//                label = strings.addressLabel,
//                value = address ?: strings.emptyField,
//                isLast = true,
//                colors = colors
//            )
        }
    }
}

// ─── Ligne label → valeur ─────────────────────────────────────────────────────

@Composable
private fun InfoRow(
    label: String,
    value: String,
    isLast: Boolean,
    colors: AuthColors,
) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(leftRight = 24.px, topBottom = 18.px),
            verticalAlignment = Alignment.Top,
        ) {
            SpanText(
                label,
                modifier = Modifier
                    .width(160.px)
                    .fontSize(AuthTypography.small)
                    .color(colors.mutedText)
                    .styleModifier { property("flex-shrink", "0") }
            )
            SpanText(
                value,
                modifier = Modifier
                    .fontSize(AuthTypography.small)
                    .fontWeight(FontWeight.Medium)
                    .color(colors.onBackground)
            )
        }
        if (!isLast) {
            Box(modifier = Modifier.fillMaxWidth().height(1.px).backgroundColor(colors.border))
        }
    }
}